import { pdf } from '@react-pdf/renderer';

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Popper from 'components/global/Popper';
import * as D from 'components/list/DynamicListFilter.styled';

import useMediaQuery from 'hooks/useMediaQuery';

import MainTemplate from 'pages/Shipments/PrintOrders/MainTemplate';

import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import { faColumns } from '@fortawesome/pro-solid-svg-icons';

import ColumnList from '../../ColumnSelector/ColumnList';
import * as S from '../../Print.styled';
import usePrintUtils, { barcodeSizes, defaultOrderData, fontSizes, fontWeights } from '../../hooks/usePrintUtils';
import ConfigurationOptionEnumList from '../common/ConfigurationOptionEnumList';
import ConfigurationOptionList from '../common/ConfigurationOptionList';
import OrderAutocomplete from '../common/OrderAutocomplete';

const configOptions = [
	{
		type: 'enum',
		name: 'templateName',
		data: ['template1', 'template2', 'template3', 'template4', 'template5'],
		toolTip: 'onboarding.printTemplateTitle'
	},
	{
		type: 'enum',
		name: 'pageSize',
		data: ['A4', 'A5', 'A6', 'A7'],
		toolTip: 'print.templatePageSize'
	},
	{
		type: 'enum',
		name: 'fontSize',
		data: Object.keys(fontSizes),
		toolTip: 'print.templateFontSize'
	},
	{
		type: 'enum',
		name: 'fontWeight',
		data: Object.keys(fontWeights),
		toolTip: 'print.templateFontWeight'
	},
	{
		type: 'enum',
		name: 'barcodeSize',
		data: Object.keys(barcodeSizes),
		toolTip: 'print.templateBarcodeSize'
	},
	{
		type: 'enum',
		name: 'language',
		data: languages.map((lang) => lang.code),
		toolTip: 'print.templateLanguage'
	},
	{
		type: 'boolean',
		name: 'showQrCode',
		toolTip: 'print.templateShowQrCode'
	},
	{
		type: 'boolean',
		name: 'showBarcode'
	},
	{
		type: 'boolean',
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		name: 'showOrderDate'
	},
	{
		type: 'boolean',
		name: 'showInvoiceCreatedDate'
	},
	{
		type: 'boolean',
		name: 'showDateOfShipmentArrival'
	},
	{
		type: 'boolean',
		name: 'showDeliveryCompany'
	},
	{
		type: 'boolean',
		name: 'showShipmentNumber'
	},
	{
		type: 'boolean',
		name: 'showPaymentMethod'
	},
	{
		type: 'boolean',
		name: 'showVatNumber'
	},
	{
		type: 'boolean',
		name: 'showCRNumber'
	},
	{
		type: 'boolean',
		name: 'showFullName'
	},
	{
		type: 'boolean',
		name: 'showPhoneNumber'
	},
	{
		type: 'boolean',
		name: 'showAddress'
	},
	{
		type: 'boolean',
		name: 'showCity'
	},
	{
		type: 'boolean',
		name: 'showSenderName'
	},
	{
		type: 'boolean',
		name: 'showSenderAddress'
	},
	{
		type: 'boolean',
		name: 'showOrderItems'
	},
	{
		type: 'boolean',
		name: 'showTotalQuantity'
	},
	{
		type: 'boolean',
		name: 'showSubTotal'
	},
	{
		type: 'boolean',
		name: 'showDiscount'
	},
	{
		type: 'boolean',
		name: 'showCodFee'
	},
	{
		type: 'boolean',
		name: 'showShipFee'
	},
	{
		type: 'boolean',
		name: 'showVatAmount'
	},
	{
		type: 'boolean',
		name: 'showTotalDue'
	},
	{
		type: 'boolean',
		name: 'showGrandTotal'
	},
	{
		type: 'boolean',
		name: 'showOrderComment'
	},
	{
		type: 'boolean',
		name: 'showCouponCode'
	}
];

const BranchLabelTemplateForm = ({ formMethods, fieldName, propJSON, isLanguageFetched }) => {
	// Util hooks
	const { setValue } = formMethods;

	const {
		orderData,
		qrCode,
		columns,
		isLoading,
		pdfFile,
		setColumns,
		setOrderData,
		handleSingleOrderChange,
		trackAndSetColumnSettings,
		mergePDF,
		handlePDFDownload
	} = usePrintUtils({
		propJSON
	});
	const { t } = useTranslation();

	const companySettings = useSelector((state) => state?.user?.generalSettings) ?? null;
	const { mobile = false } = useSelector((state) => state?.layout);
	const columnSetter = useMemo(() => trackAndSetColumnSettings(), []);

	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	const loading = useMemo(() => isLoading || !!pdfFile === false, [isLoading, pdfFile]);

	// Drag drop ref
	const [columnSettingsRef, setColumnSettingsRef] = useState(null);

	const createPDF = async (data) => {
		if (!data) return;
		await mergePDF(async (merger) => {
			const pdfBlob = await pdf(
				React.createElement(
					MainTemplate,
					{ ...data },
					{
						key: data?.orderID
					}
				)
			).toBlob();
			await merger.add(pdfBlob);
			const mergedPdf = await merger.saveAsBlob();
			return mergedPdf;
		});
	};

	// Effects
	useLayoutEffect(() => {
		if (columns.length) {
			columnSetter(columns, fieldName, setValue);
		}
	}, [columns]);

	useEffect(() => {
		const conditions = [
			!!propJSON,
			!!companySettings,
			orderData !== null && orderData !== undefined,
			isLanguageFetched === true
		];

		if (Object.hasOwnProperty.call(propJSON, 'showQrCode')) {
			conditions.push(!!qrCode);
		}
		const result = conditions.every((condition) => condition);

		if (!result) return;
		const renderData = {
			...orderData,
			companyLogo: companySettings?.CompanyLogo ?? null,
			qrCode,
			propJSON,
			fieldName
		};

		const timeout = setTimeout(() => createPDF(renderData), 500);
		return () => clearTimeout(timeout);
	}, [JSON.stringify(propJSON), companySettings, orderData, qrCode, isLanguageFetched]);

	return (
		<Form
			formMethods={formMethods}
			borderless
			style={{
				padding: '0.25rem'
			}}>
			<S.TemplateContainer>
				<S.PdfContainer>
					<>
						{mobile && pdfFile && (
							<FormItem
								colSpan={{
									md: 2
								}}>
								<Button fullWidth={true} onClick={() => handlePDFDownload(pdfFile, `AWB-Template`)}>
									{t('download')}
								</Button>
							</FormItem>
						)}
						{!isMobileDevice && (
							<div
								style={{
									height: '80dvh',
									width: '100%'
								}}
								dangerouslySetInnerHTML={{
									__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
								}}
							/>
						)}
					</>
				</S.PdfContainer>

				<S.ConfigContainer>
					<S.ConfigEnums>
						<OrderAutocomplete
							label="orderNo"
							toolTip="print.selectOrder"
							isLoading={loading}
							handleChange={handleSingleOrderChange}
							generateOrderData={setOrderData}
							defaultOrderData={defaultOrderData}
						/>

						<FormItem label="title" toolTip="print.changeDocumentTitle" isLoading={loading}>
							<StringInput placeholder="title" name={`${fieldName}.title`} />
						</FormItem>
						<ConfigurationOptionEnumList
							configOptions={configOptions}
							fieldName={fieldName}
							formMethods={formMethods}
							isLoading={loading}
							propJSON={propJSON}
						/>
						<FormItem label="orderItems" toolTip="print.templateShowOrderItems">
							<S.ColumnSelectorButton
								ref={setColumnSettingsRef}
								label="showHideSortItems"
								icon={faColumns}
								variant="borderless"
								disabled={!propJSON?.showOrderItems}
								disabledTooltip={!propJSON?.showOrderItems && 'printSettings.activateItems'}
							/>
						</FormItem>
						{columnSettingsRef && propJSON?.showOrderItems && (
							<Popper refElement={columnSettingsRef} placement="bottom-end">
								{() => (
									<D.ColumnsMenu>
										<DndProvider backend={HTML5Backend}>
											<ColumnList items={propJSON?.columnSettings} setItems={setColumns} />
										</DndProvider>
									</D.ColumnsMenu>
								)}
							</Popper>
						)}
					</S.ConfigEnums>
					<S.ConfigOptions>
						<ConfigurationOptionList
							configOptions={configOptions}
							fieldName={fieldName}
							isLoading={loading}
						/>
					</S.ConfigOptions>
				</S.ConfigContainer>
			</S.TemplateContainer>
		</Form>
	);
};

export default BranchLabelTemplateForm;
